import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle, FiLayers
} from 'react-icons/fi';
import { colors, spacing, borderRadius, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, LineSelect, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow,
  SortableTh, SortContext
} from '../../components/SharedStyles';
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getItemsCached, getVendorsCached, getAccountsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import { useLocation } from 'react-router-dom';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ItemSelect from '../../components/ItemSelect';
import VendorSelect from '../../components/VendorSelect';
import {
  createTransaction, updateTransaction, deleteTransaction,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import { generateStockFlow, deleteStockFlow, getItemStockCached, invalidateItemStock, getAvailableBatches, getAvailableSerials, listStockFlowRows, applyEntriesToStockCache } from '../../utils/stockFlowHelper';
import { costRowsChange } from '../../utils/itemCostEngine';
import LineUnitPicker from '../../components/LineUnitPicker';
import { applyUnitChangeToLine, enrichLineWithUnit, conversionFactor } from '../../utils/unitConversion';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import { generateSubTransactions, deleteSubTransactions, listSubTransactionIds } from '../../utils/subTransactionHelper';
import { getLinkedAccount } from '../../utils/coaInitializer';
import { SYSTEM_ACCOUNTS } from '../../config/tranTypes';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';

const TRAN_TYPE_CODE = 'PRO';

// INV-15: save the document and the rows `addRows` adds in ONE commit
// (transactionHelper runs it inside its own save).
const saveWithRows = async (save, addRows) => {
  // The helper always runs extraWrites inside its save. If it did not here,
  // this call was merged into a save already running (double click) — that
  // save added the rows, so nothing is added again.
  return save({ extraWrites: (w, ctx) => addRows(w, ctx) });
};

// INV-15: a new document keeps ONE pre-generated id until it is saved (`pend`),
// so a retry after a dropped connection can never save it twice — if the earlier
// attempt did reach the server, that saved document is returned instead.
const savedEarlier = async (companyId, pend) => {
  if (!pend.tried) { pend.tried = true; return null; }
  const snap = await getDoc(doc(db, 'companies', companyId, 'transactions', pend.id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Footer summary spec — aggregated server-side in paged mode,
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Planned', 'Completed']
};

const EMPTY_LINE = {
  componentItemId: '', description: '', uom: '', unitCode: '', scrapPercent: 0, isCritical: false,
  estQty: 0, estUnitCost: 0, estLineCost: 0,
  actualQty: 0, actualUnitCost: 0, actualLineCost: 0,
  trackingType: 'None', batchNumber: '', serialNumbers: []
};

const EMPTY_OVERHEAD = {
  expenseType: 'Labour', accountId: '', accountCode: '', accountName: '',
  allocationMethod: 'Fixed', description: '', estAmount: 0, actualAmount: 0
};

const EMPTY_OUTSOURCING = {
  vendorId: '', vendorName: '', accountId: '', accountCode: '', accountName: '',
  description: '', costMethod: 'Per Unit', estAmount: 0, actualAmount: 0
};

const EXPENSE_TYPES = ['Labour', 'Factory Overhead', 'Utilities', 'Depreciation', 'Other'];
const ALLOCATION_METHODS = ['Per Unit', 'Fixed', 'Percentage of Material'];
const COST_METHODS = ['Per Unit', 'Fixed', 'Per Batch'];

const STATUS_COLORS = {
  Planned: colors.warning.main,
  Completed: colors.success.main,
  Cancelled: colors.error.main,
};

const ProductionOrder = () => {
  const { activeCompany, currentUser, companyUser, activeFinancialYear, activeBranch, isModuleEnabled } = useAuth();
  const blockNegative = isModuleEnabled && isModuleEnabled('inventory_block_negative');
  const { canEdit: canEditPage, canDelete: canDeletePage, canChange } = usePagePermissions('store', 'productionOrder');
  const location = useLocation();
  const isAdmin = companyUser?.role === 'admin';
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    bomId: '', bomDocNumber: '', finishedItemId: '', finishedItemName: '',
    plannedQty: 1, actualQty: 0, outputUom: '',
    completionDate: '', notes: '', status: 'Planned',
    locationId: '', locationName: '', productionType: 'In-House'
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [overheadItems, setOverheadItems] = useState([]);
  const [outsourcingItems, setOutsourcingItems] = useState([]);

  // Reference data
  const [items, setItems] = useState([]);
  const [boms, setBoms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stockBalances, setStockBalances] = useState({});
  const [availableBatches, setAvailableBatches] = useState({});
  const [availableSerials, setAvailableSerials] = useState({});
  const [printConfig, setPrintConfig] = useState(null);

  // ======================== DATA LOADERS ========================

  // The record list itself loads inside useDocListPagination above
  // (loadRecords = its refresh). The old full-history getTransactions load lived here.

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows
        .filter(i => i.isDeleted !== true)
        .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadBoms = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');
      const q = query(ref, where('tranTypeCode', '==', 'BOM'), where('status', '==', 'Active'));
      const snapshot = await getDocs(q);
      setBoms(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) { console.error('Error loading BOMs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: vendors come from the device-local master cache (0 Firestore reads/open).
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows]
        .sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) { console.error('Error loading vendors:', error); }
  }, [activeCompany]);

  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: accounts come from the device-local master cache (0 Firestore reads/open).
      const allAccts = await getAccountsCached(activeCompany.id);
      const allLvl5 = allAccts.filter(a => a.level === 5 || a.isPostable);
      setAccounts(allLvl5
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (error) { console.error('Error loading accounts:', error); }
  }, [activeCompany]);

  // Firebase cost: the whole-collection stock preload that used to live here is
  // gone. Component stock is now read per item at Completion — see shortageOf.
  // The line picker can still show stock, but only for an item the user asks about.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => {
    loadItems(); loadBoms(); loadLocations();
    loadVendors(); loadAccounts();
    loadDocumentFormats(activeCompany?.id).then(setPrintConfig);
  }, [loadItems, loadBoms, loadLocations, loadVendors, loadAccounts, activeCompany?.id]);

  // ======================== HELPERS ========================

  const getItemName = useCallback((itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? (item.itemCode ? item.itemCode + ' - ' : '') + item.itemName : '-';
  }, [items]);

  const getBomName = (bomId) => {
    const bom = boms.find(b => b.id === bomId);
    return bom ? (bom.bomName || bom.docNumber || bomId) : bomId || '-';
  };

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) ||
        r.notes?.toLowerCase().includes(q) ||
        getItemName(r.finishedItemId)?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, getItemName, sort]);

  // ======================== COST CALCULATIONS ========================

  const estCostSummary = useMemo(() => {
    const materialCost = lineItems.reduce((sum, l) => sum + (l.estLineCost || 0), 0);
    const qty = formData.plannedQty || 1;
    const overheadCost = overheadItems.reduce((sum, o) => {
      if (o.allocationMethod === 'Percentage of Material') return sum + (materialCost * (o.estAmount || 0) / 100);
      if (o.allocationMethod === 'Per Unit') return sum + ((o.estAmount || 0) * qty);
      return sum + (o.estAmount || 0); // Fixed
    }, 0);
    const outsourcingCost = outsourcingItems.reduce((sum, o) => {
      if (o.costMethod === 'Per Unit') return sum + ((o.estAmount || 0) * qty);
      return sum + (o.estAmount || 0);
    }, 0);
    const totalCost = materialCost + overheadCost + outsourcingCost;
    const costPerUnit = formData.plannedQty > 0 ? totalCost / formData.plannedQty : 0;
    return { materialCost, overheadCost, outsourcingCost, totalCost, costPerUnit };
  }, [lineItems, overheadItems, outsourcingItems, formData.plannedQty]);

  const actualCostSummary = useMemo(() => {
    const materialCost = lineItems.reduce((sum, l) => sum + (l.actualLineCost || 0), 0);
    const actualMatCost = materialCost; // for percentage calc
    const actualOutputQty = formData.actualQty || formData.plannedQty || 1;
    const overheadCost = overheadItems.reduce((sum, o) => {
      if (o.allocationMethod === 'Percentage of Material') return sum + (actualMatCost * (o.actualAmount || 0) / 100);
      if (o.allocationMethod === 'Per Unit') return sum + ((o.actualAmount || 0) * actualOutputQty);
      return sum + (o.actualAmount || 0); // Fixed
    }, 0);
    const outsourcingCost = outsourcingItems.reduce((sum, o) => {
      if (o.costMethod === 'Per Unit') return sum + ((o.actualAmount || 0) * actualOutputQty);
      return sum + (o.actualAmount || 0);
    }, 0);
    const totalCost = materialCost + overheadCost + outsourcingCost;
    const costPerUnit = actualOutputQty > 0 ? totalCost / actualOutputQty : 0;
    return { materialCost, overheadCost, outsourcingCost, totalCost, costPerUnit };
  }, [lineItems, overheadItems, outsourcingItems, formData.actualQty, formData.plannedQty]);

  // ======================== FORM HANDLERS ========================

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleBomChange = (bomId) => {
    const bom = boms.find(b => b.id === bomId);
    if (bom) {
      const finishedItem = items.find(i => i.id === bom.finishedItemId);
      setFormData(prev => ({
        ...prev, bomId, bomDocNumber: bom.docNumber || '',
        finishedItemId: bom.finishedItemId || '',
        finishedItemName: finishedItem?.itemName || '',
        outputUom: bom.outputUom || finishedItem?.unitOfMeasure || '',
        productionType: bom.productionType || 'In-House',
        locationId: bom.locationId || prev.locationId,
        locationName: bom.locationName || prev.locationName
      }));

      const plannedQty = formData.plannedQty || 1;
      const outputQty = bom.outputQuantity || 1;
      const ratio = plannedQty / outputQty;

      // Populate component lines with estimated costs
      const bomLines = (bom.lines || []).map(l => {
        const baseQty = (l.quantity || 0) * ratio;
        const scrap = l.scrapPercent || 0;
        const estQty = Math.round(baseQty * (1 + scrap / 100) * 10000) / 10000;
        const estUnitCost = l.unitCost || 0;
        // ACTUAL component cost = the item's CURRENT average cost (not the BOM's frozen
        // cost), so finished-goods cost reflects today's real material cost. Estimated
        // stays at the BOM cost so Production Variance = planned (BOM) vs actual (current).
        const compItem = items.find(i => i.id === (l.itemId || l.componentItemId));
        const actUnitCost = compItem?.avgCost || compItem?.averageCost || compItem?.purchasePrice || estUnitCost || 0;
        return {
          ...EMPTY_LINE,
          componentItemId: l.itemId || l.componentItemId || '',
          description: l.description || l.itemName || '',
          uom: l.uom || '',
          scrapPercent: scrap,
          isCritical: l.isCritical || false,
          estQty, estUnitCost, estLineCost: estQty * estUnitCost,
          actualQty: 0, actualUnitCost: actUnitCost, actualLineCost: 0
        };
      });
      setLineItems(bomLines.length > 0 ? bomLines : [{ ...EMPTY_LINE }]);

      // Populate overhead from BOM
      const bomOverheads = (bom.overheadExpenses || []).map(o => ({
        ...EMPTY_OVERHEAD,
        expenseType: o.expenseType || 'Other',
        accountId: o.accountId || '', accountCode: o.accountCode || '', accountName: o.accountName || '',
        allocationMethod: o.allocationMethod || 'Fixed',
        description: o.description || '',
        estAmount: o.amount || 0, actualAmount: 0
      }));
      setOverheadItems(bomOverheads);

      // Populate outsourcing from BOM
      const bomOutsourcing = (bom.outsourcingItems || []).map(o => ({
        ...EMPTY_OUTSOURCING,
        vendorId: o.vendorId || '', vendorName: o.vendorName || '',
        accountId: o.accountId || '', accountCode: o.accountCode || '', accountName: o.accountName || '',
        description: o.description || '',
        costMethod: o.costMethod || 'Per Unit',
        estAmount: o.amount || 0, actualAmount: 0
      }));
      setOutsourcingItems(bomOutsourcing);
    } else {
      setFormData(prev => ({ ...prev, bomId: '', bomDocNumber: '', finishedItemId: '', finishedItemName: '' }));
      setLineItems([{ ...EMPTY_LINE }]);
      setOverheadItems([]);
      setOutsourcingItems([]);
    }
  };

  const recalcEstQty = (lines, plannedQty) => {
    const bom = boms.find(b => b.id === formData.bomId);
    if (!bom) return lines;
    const outputQty = bom.outputQuantity || 1;
    const ratio = plannedQty / outputQty;
    const bomLines = bom.lines || [];
    return lines.map((line, i) => {
      const bomLine = bomLines[i];
      if (bomLine) {
        const baseQty = (bomLine.quantity || 0) * ratio;
        const scrap = bomLine.scrapPercent || line.scrapPercent || 0;
        const estQty = Math.round(baseQty * (1 + scrap / 100) * 10000) / 10000;
        const estLineCost = estQty * (line.estUnitCost || 0);
        return { ...line, estQty, estLineCost };
      }
      return line;
    });
  };

  const handlePlannedQtyChange = (value) => {
    const plannedQty = Number(value) || 0;
    setFormData(prev => ({ ...prev, plannedQty }));
    setLineItems(prev => recalcEstQty(prev, plannedQty));
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].componentItemId) {
        const item = items.find(i => i.id === updated[index].componentItemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index].uom = patch.unitCode;
          updated[index].unitCode = patch.unitCode;
          updated[index].actualUnitCost = patch.unitCost;
        }
      }
      if (field === 'componentItemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          updated[index].uom = item.unitOfMeasure || '';
          updated[index].estUnitCost = item.avgCost || item.purchasePrice || 0;
          updated[index].actualUnitCost = updated[index].estUnitCost;
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          // Load batch/serial data for tracked items (FIFO auto-fill for batch)
          if (item.trackingType === 'Batch') {
            getAvailableBatches(activeCompany.id, value).then(batches => {
              setAvailableBatches(prev => ({ ...prev, [value]: batches }));
              if (batches.length > 0) {
                setLineItems(prev => {
                  const u = [...prev];
                  if (u[index] && u[index].componentItemId === value) {
                    u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  }
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial') {
            getAvailableSerials(activeCompany.id, value).then(serials => {
              setAvailableSerials(prev => ({ ...prev, [value]: serials }));
            });
          }
        }
      }
      // Recalculate line costs
      if (['estQty', 'estUnitCost'].includes(field)) {
        updated[index].estLineCost = (updated[index].estQty || 0) * (updated[index].estUnitCost || 0);
      }
      if (['actualQty', 'actualUnitCost'].includes(field)) {
        updated[index].actualLineCost = (updated[index].actualQty || 0) * (updated[index].actualUnitCost || 0);
      }
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  const newDocRef = useRef(null); // INV-15: { id, tried } of the new order being saved
  const saveLockRef = useRef(false); // one save at a time (double-click guard)
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.componentItemId || (Number(line.actualQty) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  // Overhead handlers
  const handleOverheadChange = (index, field, value) => {
    setOverheadItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'accountId' && value) {
        const acc = accounts.find(a => a.id === value);
        if (acc) { updated[index].accountCode = acc.code || ''; updated[index].accountName = acc.name || ''; }
      }
      return updated;
    });
  };
  const addOverhead = () => setOverheadItems(prev => [...prev, { ...EMPTY_OVERHEAD }]);
  const removeOverhead = (index) => setOverheadItems(prev => prev.filter((_, i) => i !== index));

  // Outsourcing handlers
  const handleOutsourcingChange = async (index, field, value) => {
    setOutsourcingItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'vendorId' && value) {
        const vendor = vendors.find(v => v.id === value);
        if (vendor) updated[index].vendorName = vendor.vendorName || '';
      }
      return updated;
    });
    // Auto-resolve vendor's linked payable account
    if (field === 'vendorId' && value && activeCompany?.id) {
      try {
        const linkedAccount = await getLinkedAccount(activeCompany.id, 'vendor', value);
        if (linkedAccount) {
          setOutsourcingItems(prev => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              accountId: linkedAccount.id || '',
              accountCode: linkedAccount.code || '',
              accountName: linkedAccount.name || ''
            };
            return updated;
          });
        }
      } catch (err) { console.error('Error resolving vendor account:', err); }
    }
  };
  const addOutsourcing = () => setOutsourcingItems(prev => [...prev, { ...EMPTY_OUTSOURCING }]);
  const removeOutsourcing = (index) => setOutsourcingItems(prev => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      bomId: '', bomDocNumber: '', finishedItemId: '', finishedItemName: '',
      plannedQty: 1, actualQty: 0, outputUom: '',
      completionDate: '', notes: '', status: 'Planned',
      locationId: '', locationName: '', productionType: 'In-House'
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setOverheadItems([]);
    setOutsourcingItems([]);
    setEditingRecord(null);
    newDocRef.current = null;
  };

  // ======================== ACTIONS ========================

  const handleCreate = () => { resetForm(); setViewMode('form'); };

  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({
      docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0],
      bomId: rec.bomId || '', bomDocNumber: rec.bomDocNumber || '',
      finishedItemId: rec.finishedItemId || '', finishedItemName: rec.finishedItemName || '',
      plannedQty: rec.plannedQty || 1, actualQty: rec.actualQty || 0,
      outputUom: rec.outputUom || '',
      completionDate: rec.completionDate || '',
      notes: rec.notes || '', status: rec.status || 'Planned',
      locationId: rec.locationId || '', locationName: rec.locationName || '',
      productionType: rec.productionType || 'In-House'
    });
    setLineItems(rec.lines?.length > 0
      ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l }))
      : [{ ...EMPTY_LINE }]);
    setOverheadItems(rec.overheadExpenses?.length > 0
      ? rec.overheadExpenses.map(o => ({ ...EMPTY_OVERHEAD, ...o }))
      : []);
    setOutsourcingItems(rec.outsourcingItems?.length > 0
      ? rec.outsourcingItems.map(o => ({ ...EMPTY_OUTSOURCING, ...o }))
      : []);
    setViewMode('form');
  };

  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Handle navigation from reports. With pagination the target may not be in
  // the loaded page — ensureDoc fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        setViewingRecord(rec);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Auto-fill actual quantities from estimated when completing
  const autoFillActuals = () => {
    setLineItems(prev => prev.map(l => ({
      ...l,
      actualQty: l.actualQty || l.estQty || 0,
      actualUnitCost: l.actualUnitCost || l.estUnitCost || 0,
      actualLineCost: (l.actualQty || l.estQty || 0) * (l.actualUnitCost || l.estUnitCost || 0)
    })));
    setOverheadItems(prev => prev.map(o => ({
      ...o, actualAmount: o.actualAmount || o.estAmount || 0
    })));
    setOutsourcingItems(prev => prev.map(o => ({
      ...o, actualAmount: o.actualAmount || o.estAmount || 0
    })));
    setFormData(prev => ({
      ...prev,
      actualQty: prev.actualQty || prev.plannedQty || 0,
      completionDate: prev.completionDate || new Date().toISOString().split('T')[0]
    }));
  };

  const handleSave = async (saveStatus) => {
    if (saveLockRef.current) return;
    saveLockRef.current = true;
    try { return await handleSaveOnce(saveStatus); } finally { saveLockRef.current = false; }
  };

  const handleSaveOnce = async (saveStatus) => {
    if (!canEditPage) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.bomId) { alert('Please select a BOM'); return; }
    if (!formData.plannedQty || formData.plannedQty <= 0) { alert('Planned quantity must be greater than 0'); return; }
    const validLines = lineItems.filter(l => l.componentItemId);
    if (validLines.length === 0) { alert('Please add at least one component item'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year. Please set up a financial year first.'); return; }

    // Financial year date check
    const docDateObj = new Date(formData.docDate);
    const fyStart = activeFinancialYear.startDate?.toDate ? activeFinancialYear.startDate.toDate() : new Date(activeFinancialYear.startDate);
    const fyEnd = activeFinancialYear.endDate?.toDate ? activeFinancialYear.endDate.toDate() : new Date(activeFinancialYear.endDate);
    if (docDateObj < fyStart || docDateObj > fyEnd) {
      alert(`Document date must be within the active financial year (${fyStart.toLocaleDateString()} - ${fyEnd.toLocaleDateString()})`);
      return;
    }

    // Critical items stock check before completing
    if (saveStatus === 'Completed') {
      if (!formData.actualQty && !formData.plannedQty) {
        alert('Actual output quantity is required for completion'); return;
      }
      // Compare in BASE units. Stock is read per component below; needed qty is
      // in the line's chosen unit, so multiply by conversion factor first.
      // When inventory_block_negative is ON, every line is checked (not just
      // critical) and any shortage is a hard block.
      const baseConversion = (line) => {
        const item = items.find(i => i.id === line.componentItemId);
        const u = item?.units?.find(x => x.unitCode === (line.unitCode || line.uom));
        return Number(u?.conversion) || 1;
      };
      // Firebase cost: stock is fetched here for the COMPONENT LINES ONLY
      // (a handful of per-item reads, cached), instead of the old approach of
      // downloading the company's entire stock history on every form open just
      // to have these numbers ready. Each figure is read live at the moment of
      // completion, so it is also fresher than the old preloaded map.
      const stockBranchId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined;
      const shortageOf = async (lines) => {
        const checked = await Promise.all(lines.map(async (l) => {
          let availableBase = 0;
          try {
            availableBase = await getItemStockCached(activeCompany.id, l.componentItemId, { branchId: stockBranchId });
          } catch { availableBase = 0; }
          const neededBase = (l.actualQty || l.estQty || 0) * baseConversion(l);
          return { line: l, availableBase, short: availableBase < neededBase };
        }));
        return checked.filter(c => c.short);
      };
      const formatShortages = (list) => list.map(({ line: l, availableBase }) => {
        const conv = baseConversion(l);
        const availableEntered = conv > 0 ? availableBase / conv : availableBase;
        const unit = l.unitCode || l.uom || '';
        return `${l.description}: need ${l.actualQty || l.estQty} ${unit}, available ${availableEntered.toFixed(2)} ${unit}`;
      }).join('\n');

      if (blockNegative) {
        const allShortages = await shortageOf(validLines);
        if (allShortages.length > 0) {
          alert(`Cannot complete — would drive stock below zero:\n\n${formatShortages(allShortages)}`);
          return;
        }
      } else {
        const criticalShortages = await shortageOf(validLines.filter(l => l.isCritical));
        if (criticalShortages.length > 0) {
          if (!window.confirm(`Warning: Critical items have insufficient stock:\n\n${formatShortages(criticalShortages)}\n\nContinue anyway?`)) return;
        }
      }
      // Validate batch/serial tracking for consumed materials
      for (const line of validLines) {
        const qty = line.actualQty || line.estQty || 0;
        if (qty <= 0) continue;
        if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
          alert(`Batch number is required for "${line.description}" (Batch tracked item)`); return;
        }
        if (line.trackingType === 'Serial') {
          const serials = line.serialNumbers || [];
          if (serials.length !== qty) {
            alert(`"${line.description}" requires exactly ${qty} serial number(s). You selected ${serials.length}.`); return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const status = saveStatus || 'Planned';
      const actualQty = status === 'Completed'
        ? (formData.actualQty || formData.plannedQty)
        : formData.actualQty;

      // Build final lines
      const finalLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.componentItemId);
        const line = { ...l, lineNumber: i + 1 };
        // Auto-fill actuals on completion if not set
        if (status === 'Completed') {
          if (!line.actualQty) line.actualQty = line.estQty || 0;
          if (!line.actualUnitCost) line.actualUnitCost = line.estUnitCost || 0;
          line.actualLineCost = (line.actualQty || 0) * (line.actualUnitCost || 0);
        }
        // Add item metadata for stock flow
        line.itemId = l.componentItemId;
        line.itemCode = item?.itemCode || '';
        line.itemName = item?.itemName || l.description || '';
        line.unitOfMeasure = item?.unitOfMeasure || l.uom || 'Pcs';
        line.quantity = line.actualQty || line.estQty || 0;
        line.unitPrice = line.actualUnitCost || line.estUnitCost || 0;
        line.unitCost = line.actualUnitCost || line.estUnitCost || 0;
        return line;
      });

      // Build overhead/outsourcing with auto-fill on completion
      const finalOverheads = overheadItems.map(o => {
        if (status === 'Completed' && !o.actualAmount) return { ...o, actualAmount: o.estAmount || 0 };
        return { ...o };
      });
      const finalOutsourcing = outsourcingItems.map(o => {
        if (status === 'Completed' && !o.actualAmount) return { ...o, actualAmount: o.estAmount || 0 };
        return { ...o };
      });

      // Calculate actual cost summary for saving
      const actMatCost = finalLines.reduce((s, l) => s + (l.actualLineCost || 0), 0);
      const actOhCost = finalOverheads.reduce((s, o) => {
        if (o.allocationMethod === 'Percentage of Material') return s + (actMatCost * (o.actualAmount || 0) / 100);
        if (o.allocationMethod === 'Per Unit') return s + ((o.actualAmount || 0) * (actualQty || 1));
        return s + (o.actualAmount || 0); // Fixed
      }, 0);
      const actOsCost = finalOutsourcing.reduce((s, o) => {
        if (o.costMethod === 'Per Unit') return s + ((o.actualAmount || 0) * (actualQty || 1));
        return s + (o.actualAmount || 0);
      }, 0);
      const actTotalCost = actMatCost + actOhCost + actOsCost;
      const actCostPerUnit = actualQty > 0 ? actTotalCost / actualQty : 0;

      // Estimated cost
      const estMatCost = finalLines.reduce((s, l) => s + (l.estLineCost || 0), 0);
      const plannedQty = formData.plannedQty || 1;
      const estOhCost = finalOverheads.reduce((s, o) => {
        if (o.allocationMethod === 'Percentage of Material') return s + (estMatCost * (o.estAmount || 0) / 100);
        if (o.allocationMethod === 'Per Unit') return s + ((o.estAmount || 0) * plannedQty);
        return s + (o.estAmount || 0); // Fixed
      }, 0);
      const estOsCost = finalOutsourcing.reduce((s, o) => {
        if (o.costMethod === 'Per Unit') return s + ((o.estAmount || 0) * plannedQty);
        return s + (o.estAmount || 0);
      }, 0);
      const estTotalCost = estMatCost + estOhCost + estOsCost;
      const estCostPerUnit = formData.plannedQty > 0 ? estTotalCost / formData.plannedQty : 0;

      const data = {
        docDate: formData.docDate,
        bomId: formData.bomId,
        bomDocNumber: formData.bomDocNumber,
        finishedItemId: formData.finishedItemId,
        finishedItemName: formData.finishedItemName,
        plannedQty: formData.plannedQty,
        actualQty: actualQty,
        outputUom: formData.outputUom,
        completionDate: status === 'Completed'
          ? (formData.completionDate || new Date().toISOString().split('T')[0])
          : formData.completionDate,
        locationId: formData.locationId,
        locationName: formData.locationName,
        productionType: formData.productionType,
        notes: formData.notes,
        status,
        lines: finalLines,
        overheadExpenses: finalOverheads,
        outsourcingItems: finalOutsourcing,
        // Cost summary
        estMaterialCost: estMatCost, estOverheadCost: estOhCost, estOutsourcingCost: estOsCost,
        estTotalCost, estCostPerUnit,
        actualMaterialCost: actMatCost, actualOverheadCost: actOhCost, actualOutsourcingCost: actOsCost,
        actualTotalCost: actTotalCost, actualCostPerUnit: actCostPerUnit,
        // Variance
        materialVariance: actMatCost - estMatCost,
        overheadVariance: actOhCost - estOhCost,
        outsourcingVariance: actOsCost - estOsCost,
        totalVariance: actTotalCost - estTotalCost,
        // Yield
        yieldPercent: formData.plannedQty > 0 ? ((actualQty || 0) / formData.plannedQty * 100) : 0,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null
      };

      // On completion: stock flow + accounting + item cost update.
      // PRO is manualStockFlow/manualAccounting, so the central save deliberately
      // skips building these — this page builds them. INV-15: they are now added
      // to the SAME all-or-nothing save as the order (they used to be separate
      // saves after it: a dropped connection left a Completed order with no stock
      // or no accounting). An edit REPLACES the order's earlier rows in that save,
      // so re-completing an order never stacks a duplicate stock set (spec Rule #5
      // / §2.4, the 26→28-Apr bug). Costs and quantities are built exactly as before.
      let addRows = null;
      if (status === 'Completed') {
        // Stock movements: raw materials OUT + finished good IN in ONE call.
        // They MUST share a single generateStockFlow call because its deterministic
        // per-line IDs are `${transactionId}__sf${index}`. Two separate calls both
        // restarted the index at sf0, so the finished-good IN row overwrote the
        // first component's OUT row → the first raw material never deducted.
        // Combining them gives every movement its own index (sf0, sf1, sf2…).
        // No top-level `direction` is passed: each line carries its own direction,
        // exactly as before, so OUT/IN behaviour and costing are unchanged.
        // INV-02 fix (2026-09-30): quantities are entered in the line's chosen
        // unit (e.g. Ctn). Stock is kept in BASE units, so every movement goes
        // through the same unit conversion as invoices (enrichLineWithUnit).
        // Before, "2 Ctn" deducted 2 base pieces. Value (qty × cost) is unchanged.
        const itemById = (id) => items.find(i => i.id === id);
        const movementLines = finalLines.filter(l => (l.actualQty || l.estQty) > 0).map(l => {
          const itemId = l.componentItemId || l.itemId;
          return enrichLineWithUnit(itemById(itemId), {
            itemId,
            itemCode: l.itemCode || '',
            itemName: l.itemName || '',
            description: l.description,
            quantity: l.actualQty || l.estQty || 0,
            unitCode: l.unitCode || l.uom || l.unitOfMeasure || undefined,
            unitOfMeasure: l.unitOfMeasure || l.uom || 'Pcs',
            unitCost: l.actualUnitCost || l.estUnitCost || 0,
            unitPrice: l.actualUnitCost || l.estUnitCost || 0,
            direction: 'out',
            batchNumber: l.batchNumber || '',
            serialNumbers: l.serialNumbers || []
          });
        });

        if ((actualQty || 0) > 0 && formData.finishedItemId) {
          const finishedItem = items.find(i => i.id === formData.finishedItemId);
          movementLines.push(enrichLineWithUnit(finishedItem, {
            itemId: formData.finishedItemId,
            itemCode: finishedItem?.itemCode || '',
            itemName: finishedItem?.itemName || '',
            description: finishedItem?.itemName || 'Finished Product',
            quantity: actualQty,
            unitCode: formData.outputUom || undefined,
            unitOfMeasure: finishedItem?.unitOfMeasure || formData.outputUom || 'Pcs',
            unitCost: actCostPerUnit,
            unitPrice: actCostPerUnit,
            direction: 'in'
          }));
        }

        addRows = async (w, { transactionId, docNumber }) => {
          const companyId = activeCompany.id;
          // The order's earlier rows (an edit) are replaced in this same save.
          const [oldStockRows, oldSubIds, newStockRows] = await Promise.all([
            editingRecord ? listStockFlowRows(companyId, transactionId) : Promise.resolve([]),
            editingRecord ? listSubTransactionIds(companyId, transactionId) : Promise.resolve([]),
            movementLines.length > 0
              ? generateStockFlow(companyId, {
                transactionId, tranTypeCode: TRAN_TYPE_CODE,
                docNumber, docDate: formData.docDate,
                status: 'Completed',
                locationId: formData.locationId || null,
                locationName: formData.locationName || null,
                financialYearId: activeFinancialYear.id,
                branchId: activeBranch?.id || null,
                lines: movementLines
              }, currentUser.uid, { writer: w })
              : Promise.resolve([]),
          ]);

          // 3. Resolve vendor accounts for outsourcing entries missing accountId
          // (on copies, so the saved order keeps its outsourcing lines as before)
          const outsourcingForAccounts = finalOutsourcing.map(o => ({ ...o }));
          for (const os of outsourcingForAccounts) {
            if (os.vendorId && !os.accountId && activeCompany?.id) {
              try {
                const linkedAccount = await getLinkedAccount(activeCompany.id, 'vendor', os.vendorId);
                if (linkedAccount) {
                  os.accountId = linkedAccount.id || '';
                  os.accountCode = linkedAccount.code || '';
                  os.accountName = linkedAccount.name || '';
                }
              } catch (err) { console.error('Error resolving vendor account:', err); }
            }
          }

          // Accounting entries (manual - PRO has manualAccounting: true)
          const manualLines = await buildAccountingEntries(
            activeCompany.id, actMatCost, actOhCost, actOsCost, actTotalCost,
            finalOverheads, outsourcingForAccounts, docNumber, actualQty
          );
          const newSubRows = manualLines.length > 0
            ? await generateSubTransactions(activeCompany.id, {
              transactionId, tranTypeCode: TRAN_TYPE_CODE,
              docNumber, docDate: formData.docDate,
              financialYearId: activeFinancialYear.id, status: 'Posted',
              branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null,
              manualLines
            }, currentUser.uid, { writer: w })
            : [];

          // Earlier rows not overwritten by a new row with the same id go too.
          const keptStock = new Set(newStockRows.map(r => r.id));
          oldStockRows.forEach(r => { if (!keptStock.has(r.id)) w.delete(doc(db, 'companies', companyId, 'stockFlow', r.id)); });
          const keptSubs = new Set(newSubRows.map(r => r.id));
          oldSubIds.forEach(id => { if (!keptSubs.has(id)) w.delete(doc(db, 'companies', companyId, 'subTransactions', id)); });
          if (oldStockRows.length) w.afterCommit(() => { applyEntriesToStockCache(companyId, oldStockRows, -1); invalidateItemStock(companyId); });

          // Item running averages (INV-04) — old rows out, new rows in, same save.
          try {
            await costRowsChange(companyId, w, { added: newStockRows, removed: oldStockRows, financialYearId: activeFinancialYear.id });
          } catch (e) {
            console.warn('[stock] item average not updated (Data Repair will rebuild it):', e && e.message);
          }

          // 4. Update finished item's avgCost — right after the save, best effort as
          // before (it cannot share the save: the same item's running average is
          // updated in it, and the rules check each item write as one change).
          if (formData.finishedItemId && actCostPerUnit > 0) {
            w.afterCommit(async () => {
              try {
                const itemRef = doc(db, 'companies', activeCompany.id, 'inventoryItems', formData.finishedItemId);
                // Item cost fields are per BASE unit (INV-02)
                const fgItem = items.find(i => i.id === formData.finishedItemId);
                const fgFactor = conversionFactor(fgItem, formData.outputUom) || 1;
                const baseCost = actCostPerUnit / fgFactor;
                await updateDoc(itemRef, {
                  avgCost: baseCost,
                  unitCost: baseCost,
                  lastProductionCost: baseCost,
                  lastProductionDate: formData.docDate
                });
              } catch (err) { console.error('Error updating item cost:', err); }
            });
          }
        };
      }

      let savedDocId;
      let newTranData = null;
      if (editingRecord) {
        if (addRows) await saveWithRows((opts) => updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid, opts), addRows);
        else await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        savedDocId = editingRecord.id;
      } else {
        // INV-15: the id is fixed up front, so a retry never creates a second order.
        if (!newDocRef.current) newDocRef.current = { id: doc(collection(db, 'companies', activeCompany.id, 'transactions')).id, tried: false };
        const pend = newDocRef.current;
        const earlier = await savedEarlier(activeCompany.id, pend);
        if (earlier && earlier.status !== status) {
        // The earlier attempt was saved, but as something else (e.g. a Draft)
        // — don't pretend this one went through; let the user open it.
        alert(`${earlier.docNumber} was already saved as ${earlier.status} (the connection dropped before the confirmation arrived). Open it from the list to continue.`);
        setViewMode('list'); resetForm();
        loadRecords().catch(err => console.error('Background refresh failed:', err));
        return;
      }
      if (earlier) alert(`${earlier.docNumber} was already saved (the connection dropped before the confirmation arrived) — it was not saved again.`);
        const tran = { ...data, tranTypeCode: TRAN_TYPE_CODE };
        newTranData = earlier || (addRows
          ? await saveWithRows((opts) => createTransaction(activeCompany.id, tran, currentUser.uid, { ...opts, docId: pend.id }), addRows)
          : await createTransaction(activeCompany.id, tran, currentUser.uid, { docId: pend.id }));
        savedDocId = newTranData.id;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      invalidateItemStock(activeCompany.id);
      Promise.all([loadRecords()]).catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally { setSaving(false); }
  };

  // Build accounting entries for production completion
  // Queries Firestore directly for system accounts to avoid stale state issues
  const buildAccountingEntries = async (companyId, matCost, ohCost, osCost, totalCost, overheads, outsourcing, docNumber, outputQty) => {
    const entries = [];

    // Fetch Inventory Control account directly from Firestore
    let invAccount = null;
    try {
      const accRef = collection(db, 'companies', companyId, 'accounts');
      const q = query(accRef, where('code', '==', SYSTEM_ACCOUNTS.INVENTORY_CONTROL));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        invAccount = { id: d.id, ...d.data() };
      }
    } catch (err) { console.error('Error fetching inventory account:', err); }

    // Dr. Inventory Control (finished goods at total cost)
    if (invAccount && totalCost > 0) {
      entries.push({
        accountId: invAccount.id, accountCode: invAccount.code, accountName: invAccount.name,
        debitAmount: totalCost, creditAmount: 0,
        description: `Production output ${docNumber} - ${outputQty} units`
      });
    }

    // Cr. Inventory Control (raw materials consumed)
    if (invAccount && matCost > 0) {
      entries.push({
        accountId: invAccount.id, accountCode: invAccount.code, accountName: invAccount.name,
        debitAmount: 0, creditAmount: matCost,
        description: `Raw materials consumed ${docNumber}`
      });
    }

    // Cr. Overhead expense accounts
    for (const oh of overheads) {
      let effectiveAmount = oh.actualAmount || 0;
      if (oh.allocationMethod === 'Percentage of Material') {
        effectiveAmount = matCost * (oh.actualAmount || 0) / 100;
      } else if (oh.allocationMethod === 'Per Unit') {
        effectiveAmount = (oh.actualAmount || 0) * (outputQty || 1);
      }
      if (effectiveAmount > 0 && oh.accountId) {
        entries.push({
          accountId: oh.accountId, accountCode: oh.accountCode, accountName: oh.accountName,
          debitAmount: 0, creditAmount: effectiveAmount,
          description: `${oh.expenseType} - ${docNumber}`
        });
      }
    }

    // Cr. Vendor Payable for outsourcing
    for (const os of outsourcing) {
      let effectiveAmount = os.actualAmount || 0;
      if (os.costMethod === 'Per Unit') {
        effectiveAmount = (os.actualAmount || 0) * (outputQty || 1);
      }
      if (effectiveAmount > 0 && os.accountId) {
        entries.push({
          accountId: os.accountId, accountCode: os.accountCode, accountName: os.accountName,
          debitAmount: 0, creditAmount: effectiveAmount,
          description: `Outsourced: ${os.vendorName || ''} - ${docNumber}`
        });
      }
    }

    return entries;
  };

  const handleDelete = async (rec) => {
    if (!canDeletePage) { alert('You do not have permission to delete.'); return; }
    if (rec.status === 'Cancelled') { alert('Cannot delete cancelled records'); return; }
    if (rec.status !== 'Planned' && !isAdmin) { alert('Only planned records can be deleted'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('productionOrder', data, activeCompany, printConfig, activeBranch);
  };

  const handleCancel = async (rec) => {
    if (!canEditPage) { alert('You do not have permission to save changes.'); return; }
    if (rec.status === 'Cancelled') { alert('Already cancelled'); return; }
    if (rec.status === 'Completed') {
      if (!window.confirm('This will reverse stock movements and accounting entries. Continue?')) return;
    }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      if (rec.status === 'Completed') {
        await deleteStockFlow(activeCompany.id, rec.id);
        await deleteSubTransactions(activeCompany.id, rec.id);
      }
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
      invalidateItemStock(activeCompany.id);
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const canEdit = (rec) => {
    if (rec.status === 'Cancelled') return false;
    if (isAdmin) return true;
    return ['Planned', 'Completed'].includes(rec.status);
  };

  const canDelete = (rec) => {
    if (rec.status === 'Cancelled') return false;
    if (isAdmin) return true;
    return ['Planned', 'Completed'].includes(rec.status);
  };

  // ======================== LIST VIEW ========================

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Planned">Planned</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEditPage && <TranAddButton onClick={handleCreate}><FiPlus /> New Production Order</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Production Orders</h3>
          <p>{records.length === 0 ? 'Create your first production order' : 'No records match filters'}</p>
          {records.length === 0 && canEditPage && <TranAddButton onClick={handleCreate}><FiPlus /> Create Production Order</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
          <SortableTh field="bomName">BOM</SortableTh><SortableTh field="finishedItemName">Finished Item</SortableTh>
          <SortableTh field="plannedQty" style={{ textAlign: 'right' }}>Planned</SortableTh>
          <SortableTh field="actualQty" style={{ textAlign: 'right' }}>Actual</SortableTh>
          <SortableTh field="yieldPercent" style={{ textAlign: 'right' }}>Yield%</SortableTh>
          <SortableTh field="estimatedCost" style={{ textAlign: 'right' }}>Est. Cost</SortableTh>
          <SortableTh field="actualCost" style={{ textAlign: 'right' }}>Act. Cost</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => {
            const yieldPct = rec.plannedQty > 0 ? ((rec.actualQty || 0) / rec.plannedQty * 100) : 0;
            return (
              <Tr key={rec.id}>
                <Td><strong>{rec.docNumber}</strong></Td>
                <Td>{rec.docDate}</Td>
                {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
                <Td>{getBomName(rec.bomId)}</Td>
                <Td>{getItemName(rec.finishedItemId)}</Td>
                <Td style={{ textAlign: 'right' }}>{rec.plannedQty}</Td>
                <Td style={{ textAlign: 'right' }}>{rec.actualQty || '-'}</Td>
                <Td style={{ textAlign: 'right' }}>
                  {rec.status === 'Completed'
                    ? <YieldBadge $good={yieldPct >= 95}>{yieldPct.toFixed(1)}%</YieldBadge>
                    : '-'}
                </Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(rec.estTotalCost || 0)}</Td>
                <Td style={{ textAlign: 'right' }}>{rec.status === 'Completed' ? formatAmount(rec.actualTotalCost || 0) : '-'}</Td>
                <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
                <Td style={{ textAlign: 'center' }}><ActionGroup>
                  <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                  {canChange && canEdit(rec) && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                  <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                  {canDeletePage && canDelete(rec) && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                  {canEditPage && rec.status !== 'Cancelled' && (
                    <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}><FiXCircle /></IconBtn>
                  )}
                </ActionGroup></Td>
              </Tr>
            );
          })}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Planned: <strong>{listSummary.counts.Planned || 0}</strong> | Completed: <strong>{listSummary.counts.Completed || 0}</strong></span>
      </SummaryBar>
    </>
  );

  // ======================== VIEW MODE ========================

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    const yieldPct = rec.plannedQty > 0 ? ((rec.actualQty || 0) / rec.plannedQty * 100) : 0;

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && canEdit(rec) && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Production Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>BOM</label><span>{getBomName(rec.bomId)}</span></InfoItem>
            <InfoItem><label>Finished Item</label><span>{getItemName(rec.finishedItemId)}</span></InfoItem>
            <InfoItem><label>Production Type</label><span>{rec.productionType || 'In-House'}</span></InfoItem>
            <InfoItem><label>Planned Qty</label><span>{rec.plannedQty} {rec.outputUom}</span></InfoItem>
            <InfoItem><label>Actual Qty</label><span>{rec.actualQty || '-'} {rec.outputUom}</span></InfoItem>
            <InfoItem><label>Yield</label><span>{rec.status === 'Completed' ? `${yieldPct.toFixed(1)}%` : '-'}</span></InfoItem>
            <InfoItem><label>Completion Date</label><span>{rec.completionDate || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{rec.locationName || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        {/* Components - Estimated vs Actual */}
        <FormCard>
          <SectionTitle>Components</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Component</Th><Th>UOM</Th>
            <Th style={{ textAlign: 'center' }}>Crit</Th>
            <Th style={{ textAlign: 'right' }}>Est. Qty</Th><Th style={{ textAlign: 'right' }}>Est. Cost</Th><Th style={{ textAlign: 'right' }}>Est. Total</Th>
            <Th style={{ textAlign: 'right' }}>Act. Qty</Th><Th style={{ textAlign: 'right' }}>Act. Cost</Th><Th style={{ textAlign: 'right' }}>Act. Total</Th>
            <Th style={{ textAlign: 'right' }}>Variance</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => {
              const variance = (line.actualLineCost || 0) - (line.estLineCost || 0);
              return (
                <Tr key={i}>
                  <Td>{i + 1}</Td>
                  <Td>{line.itemCode ? `${line.itemCode} - ` : ''}{line.description || line.itemName || '-'}</Td>
                  <Td>{line.uom || '-'}</Td>
                  <Td style={{ textAlign: 'center' }}>{line.isCritical ? <span style={{ color: colors.error.main, fontWeight: 'bold' }}>!</span> : '-'}</Td>
                  <Td style={{ textAlign: 'right' }}>{line.estQty || 0}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(line.estUnitCost || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(line.estLineCost || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}>{line.actualQty || 0}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(line.actualUnitCost || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(line.actualLineCost || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}><VarianceText $negative={variance > 0}>{variance !== 0 ? formatAmount(variance) : '-'}</VarianceText></Td>
                </Tr>
              );
            })}
          </tbody></Table></TableContainer>
        </FormCard>

        {/* Overhead */}
        {rec.overheadExpenses?.length > 0 && (
          <FormCard>
            <SectionTitle>Overhead Expenses</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>#</Th><Th>Expense Type</Th><Th>Account</Th><Th>Allocation</Th>
              <Th style={{ textAlign: 'right' }}>Estimated</Th>
              <Th style={{ textAlign: 'right' }}>Actual</Th>
              <Th style={{ textAlign: 'right' }}>Variance</Th>
            </tr></thead><tbody>
              {rec.overheadExpenses.map((oh, i) => {
                const variance = (oh.actualAmount || 0) - (oh.estAmount || 0);
                return (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{oh.expenseType}</Td>
                    <Td>{oh.accountCode} - {oh.accountName}</Td>
                    <Td>{oh.allocationMethod}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(oh.estAmount || 0)}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(oh.actualAmount || 0)}</Td>
                    <Td style={{ textAlign: 'right' }}><VarianceText $negative={variance > 0}>{variance !== 0 ? formatAmount(variance) : '-'}</VarianceText></Td>
                  </Tr>
                );
              })}
            </tbody></Table></TableContainer>
          </FormCard>
        )}

        {/* Outsourcing */}
        {rec.outsourcingItems?.length > 0 && (
          <FormCard>
            <SectionTitle>Outsourced Processing</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>#</Th><Th>Vendor</Th><Th>Description</Th><Th>Method</Th>
              <Th style={{ textAlign: 'right' }}>Estimated</Th>
              <Th style={{ textAlign: 'right' }}>Actual</Th>
              <Th style={{ textAlign: 'right' }}>Variance</Th>
            </tr></thead><tbody>
              {rec.outsourcingItems.map((os, i) => {
                const variance = (os.actualAmount || 0) - (os.estAmount || 0);
                return (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{os.vendorName}</Td>
                    <Td>{os.description || '-'}</Td><Td>{os.costMethod}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(os.estAmount || 0)}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(os.actualAmount || 0)}</Td>
                    <Td style={{ textAlign: 'right' }}><VarianceText $negative={variance > 0}>{variance !== 0 ? formatAmount(variance) : '-'}</VarianceText></Td>
                  </Tr>
                );
              })}
            </tbody></Table></TableContainer>
          </FormCard>
        )}

        {/* Cost Summary */}
        <FormCard>
          <SectionTitle>Cost Summary</SectionTitle>
          <CostComparisonGrid>
            <CostCompRow>
              <CostCompLabel></CostCompLabel>
              <CostCompHeader>Estimated</CostCompHeader>
              <CostCompHeader>Actual</CostCompHeader>
              <CostCompHeader>Variance</CostCompHeader>
            </CostCompRow>
            <CostCompRow>
              <CostCompLabel>Material Cost</CostCompLabel>
              <CostCompValue>{formatAmount(rec.estMaterialCost || 0)}</CostCompValue>
              <CostCompValue>{formatAmount(rec.actualMaterialCost || 0)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(rec.materialVariance || 0) > 0}>{formatAmount(rec.materialVariance || 0)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow>
              <CostCompLabel>Overhead Cost</CostCompLabel>
              <CostCompValue>{formatAmount(rec.estOverheadCost || 0)}</CostCompValue>
              <CostCompValue>{formatAmount(rec.actualOverheadCost || 0)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(rec.overheadVariance || 0) > 0}>{formatAmount(rec.overheadVariance || 0)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow>
              <CostCompLabel>Outsourcing Cost</CostCompLabel>
              <CostCompValue>{formatAmount(rec.estOutsourcingCost || 0)}</CostCompValue>
              <CostCompValue>{formatAmount(rec.actualOutsourcingCost || 0)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(rec.outsourcingVariance || 0) > 0}>{formatAmount(rec.outsourcingVariance || 0)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow $highlight>
              <CostCompLabel>Total Cost</CostCompLabel>
              <CostCompValue>{formatAmount(rec.estTotalCost || 0)}</CostCompValue>
              <CostCompValue>{formatAmount(rec.actualTotalCost || 0)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(rec.totalVariance || 0) > 0}>{formatAmount(rec.totalVariance || 0)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow $primary>
              <CostCompLabel>Cost Per Unit</CostCompLabel>
              <CostCompValue>{formatAmount(rec.estCostPerUnit || 0)}</CostCompValue>
              <CostCompValue>{formatAmount(rec.actualCostPerUnit || 0)}</CostCompValue>
              <CostCompValue><VarianceText $negative={((rec.actualCostPerUnit || 0) - (rec.estCostPerUnit || 0)) > 0}>{formatAmount((rec.actualCostPerUnit || 0) - (rec.estCostPerUnit || 0))}</VarianceText></CostCompValue>
            </CostCompRow>
          </CostComparisonGrid>
        </FormCard>

        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
      </>
    );
  };

  // ======================== FORM VIEW ========================

  const renderFormView = () => {
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Production Order'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
            {canEditPage && editingRecord?.status !== 'Completed' && (
              <SaveButton onClick={() => handleSave('Planned')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Planned'}
              </SaveButton>
            )}
            {canEditPage && (
              <SaveButton $approve onClick={() => {
                autoFillActuals();
                setTimeout(() => handleSave('Completed'), 100);
              }} disabled={saving}>
                <FiCheck /> {editingRecord?.status === 'Completed' ? 'Update & Re-post' : 'Complete'}
              </SaveButton>
            )}
          </FormActions>
        </FormHeader>

        {/* Header */}
        <FormCard>
          <SectionTitle>Production Details</SectionTitle>
          <FormGrid>
            <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
            <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
            <FormGroup><Label>BOM *</Label>
              <Select value={formData.bomId} onChange={e => handleBomChange(e.target.value)}>
                <option value="">Select BOM...</option>
                {boms.map(bom => (
                  <option key={bom.id} value={bom.id}>{bom.bomName || bom.docNumber}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup><Label>Finished Item</Label><Input value={getItemName(formData.finishedItemId)} disabled /></FormGroup>
            <FormGroup><Label>Planned Qty *</Label><Input type="number" min="1" value={formData.plannedQty} onChange={e => handlePlannedQtyChange(e.target.value)} /></FormGroup>
            <FormGroup><Label>Actual Output Qty</Label><Input type="number" min="0" value={formData.actualQty} onChange={e => handleHeaderChange('actualQty', Number(e.target.value))} placeholder="Filled on completion" /></FormGroup>
            <FormGroup><Label>Output UOM</Label><Input value={formData.outputUom} disabled /></FormGroup>
            <FormGroup><Label>Production Type</Label><Input value={formData.productionType} disabled /></FormGroup>
            <FormGroup><Label>Completion Date</Label><Input type="date" value={formData.completionDate} onChange={e => handleHeaderChange('completionDate', e.target.value)} /></FormGroup>
            <FormGroup>
              <Label>{reqLabel(activeCompany, 'Location')}</Label>
              <Select value={formData.locationId} onChange={e => {
                const loc = locations.find(l => l.id === e.target.value);
                handleHeaderChange('locationId', e.target.value);
                handleHeaderChange('locationName', loc?.locationName || '');
              }}>
                <option value="">-- Select Location --</option>
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.locationName}</option>)}
              </Select>
            </FormGroup>
            <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          </FormGrid>
        </FormCard>

        {/* Components with Est vs Actual */}
        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Components (Estimated vs Actual)</SectionTitle>
            <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
          </LineItemsHeader>
          <LineTableContainer><LineTable><thead><tr>
            <LineTh style={{ width: 32, minWidth: 32, maxWidth: 32 }}>#</LineTh>
            <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Component</LineTh>
            <LineTh style={{ width: 55, minWidth: 55, maxWidth: 55 }}>UOM</LineTh>
            <LineTh style={{ width: 45, textAlign: 'center' }}>C</LineTh>
            <LineTh style={{ width: 75, textAlign: 'right' }}>Est Qty</LineTh>
            <LineTh style={{ width: 80, textAlign: 'right' }}>Est Cost</LineTh>
            <LineTh style={{ width: 85, textAlign: 'right' }}>Est Total</LineTh>
            <LineTh style={{ width: 75, textAlign: 'right' }}>Act Qty</LineTh>
            <LineTh style={{ width: 80, textAlign: 'right' }}>Act Cost</LineTh>
            <LineTh style={{ width: 85, textAlign: 'right' }}>Act Total</LineTh>
            {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Batch</LineTh>}
            {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 170, minWidth: 170, maxWidth: 170 }}>Serials</LineTh>}
            <LineTh style={{ width: 32, minWidth: 32, maxWidth: 32 }}></LineTh>
          </tr></thead><tbody>
            {lineItems.map((line, index) => (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.componentItemId} onChange={(itemId) => handleLineChange(index, 'componentItemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
                <LineTd style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary }}>{(() => {
                  const itm = items.find(i => i.id === line.componentItemId);
                  const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                  if (itmUnits.length > 1) {
                    return <LineUnitPicker item={itm} line={{ ...line, unitOfMeasure: line.uom }} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} />;
                  }
                  return line.uom || '-';
                })()}</LineTd>
                <LineTd style={{ textAlign: 'center' }}>{line.isCritical ? <span style={{ color: colors.error.main, fontWeight: 'bold' }}>!</span> : ''}</LineTd>
                <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{line.estQty || 0}</LineTd>
                <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{formatAmount(line.estUnitCost || 0)}</LineTd>
                <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{formatAmount(line.estLineCost || 0)}</LineTd>
                <LineTd><LineInput type="number" min="0" step="0.01" value={line.actualQty} onChange={e => handleLineChange(index, 'actualQty', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
                <LineTd><LineInput type="number" min="0" step="0.01" value={line.actualUnitCost} onChange={e => handleLineChange(index, 'actualUnitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} style={{ textAlign: 'right' }} /></LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(line.actualLineCost || 0)}</LineTd>
                {lineItems.some(l => l.trackingType === 'Batch') && (
                  <LineTd>{line.trackingType === 'Batch' ? (
                    <select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '4px 6px', fontSize: '12px', minWidth: 100, border: '1px solid #ddd', borderRadius: '4px' }}>
                      <option value="">Select Batch</option>
                      {(availableBatches[line.componentItemId] || []).map(b => (
                        <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining})</option>
                      ))}
                    </select>
                  ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                )}
                {lineItems.some(l => l.trackingType === 'Serial') && (
                  <LineTd>{line.trackingType === 'Serial' ? (
                    <SerialSelectPopover
                      availableSerials={availableSerials[line.componentItemId] || []}
                      selectedSerials={line.serialNumbers || []}
                      requiredCount={line.actualQty || line.estQty || 0}
                      onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)}
                    />
                  ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                )}
                <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
              </tr>
            ))}
          </tbody></LineTable></LineTableContainer>
          <TotalsSection>
            <TotalsTable><tbody>
              <tr><td>Estimated Material</td><td>{formatAmount(estCostSummary.materialCost)}</td></tr>
              <TotalRow><td>Actual Material</td><td>{formatAmount(actualCostSummary.materialCost)}</td></TotalRow>
            </tbody></TotalsTable>
          </TotalsSection>
        </FormCard>

        {/* Overhead */}
        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Overhead Expenses</SectionTitle>
            <AddLineButton onClick={addOverhead}><FiPlus /> Add Overhead</AddLineButton>
          </LineItemsHeader>
          {overheadItems.length === 0 ? (
            <EmptySection>No overhead expenses. Add overhead or select a BOM with overhead defined.</EmptySection>
          ) : (
            <LineTableContainer><LineTable><thead><tr>
              <LineTh style={{ width: 32, minWidth: 32, maxWidth: 32 }}>#</LineTh>
              <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Type</LineTh>
              <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Account</LineTh>
              <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Allocation</LineTh>
              <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Description</LineTh>
              <LineTh style={{ width: 90, textAlign: 'right' }}>Est. Cost</LineTh>
              <LineTh style={{ width: 100, textAlign: 'right' }}>Est. Total</LineTh>
              <LineTh style={{ width: 90, textAlign: 'right' }}>Act. Cost</LineTh>
              <LineTh style={{ width: 100, textAlign: 'right' }}>Act. Total</LineTh>
              <LineTh style={{ width: 32, minWidth: 32, maxWidth: 32 }}></LineTh>
            </tr></thead><tbody>
              {overheadItems.map((oh, index) => {
                const plannedQty = formData.plannedQty || 1;
                const actualQty = formData.actualQty || plannedQty;
                const matCostEst = lineItems.reduce((s, l) => s + (l.estLineCost || 0), 0);
                const matCostAct = lineItems.reduce((s, l) => s + (l.actualLineCost || 0), 0);
                let estTotal = oh.estAmount || 0;
                let actTotal = oh.actualAmount || 0;
                if (oh.allocationMethod === 'Per Unit') {
                  estTotal = (oh.estAmount || 0) * plannedQty;
                  actTotal = (oh.actualAmount || 0) * actualQty;
                } else if (oh.allocationMethod === 'Percentage of Material') {
                  estTotal = matCostEst * (oh.estAmount || 0) / 100;
                  actTotal = matCostAct * (oh.actualAmount || 0) / 100;
                }
                return (
                <tr key={index}>
                  <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                  <LineTd>
                    <LineSelect value={oh.expenseType} onChange={e => handleOverheadChange(index, 'expenseType', e.target.value)}>
                      {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </LineSelect>
                  </LineTd>
                  <LineTd>
                    <AccountSelect value={oh.accountId} onChange={e => handleOverheadChange(index, 'accountId', e.target.value)}>
                      <option value="">-- Account --</option>
                      {accounts.filter(a => a.code?.startsWith('5') || a.code?.startsWith('6')).map(a => (
                        <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                      ))}
                    </AccountSelect>
                  </LineTd>
                  <LineTd>
                    <LineSelect value={oh.allocationMethod} onChange={e => handleOverheadChange(index, 'allocationMethod', e.target.value)}>
                      {ALLOCATION_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </LineSelect>
                  </LineTd>
                  <LineTd><LineInput value={oh.description} onChange={e => handleOverheadChange(index, 'description', e.target.value)} placeholder="Details" /></LineTd>
                  <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{formatAmount(oh.estAmount || 0)}</LineTd>
                  <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(estTotal)}</LineTd>
                  <LineTd><LineInput type="number" min="0" step="0.01" value={oh.actualAmount} onChange={e => handleOverheadChange(index, 'actualAmount', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
                  <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(actTotal)}</LineTd>
                  <LineTd style={{ textAlign: 'center' }}><RemoveLineBtn onClick={() => removeOverhead(index)}><FiX /></RemoveLineBtn></LineTd>
                </tr>
                );
              })}
            </tbody></LineTable></LineTableContainer>
          )}
        </FormCard>

        {/* Outsourcing */}
        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Outsourced Processing</SectionTitle>
            <AddLineButton onClick={addOutsourcing}><FiPlus /> Add Process</AddLineButton>
          </LineItemsHeader>
          {outsourcingItems.length === 0 ? (
            <EmptySection>No outsourced processing. Add processes or select a BOM with outsourcing defined.</EmptySection>
          ) : (
            <OverflowVisible><LineTable><thead><tr>
              <LineTh style={{ width: 32, minWidth: 32, maxWidth: 32 }}>#</LineTh>
              <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Vendor</LineTh>
              <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Description</LineTh>
              <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Method</LineTh>
              <LineTh style={{ width: 85, textAlign: 'right' }}>Est. Cost</LineTh>
              <LineTh style={{ width: 95, textAlign: 'right' }}>Est. Total</LineTh>
              <LineTh style={{ width: 85, textAlign: 'right' }}>Act. Cost</LineTh>
              <LineTh style={{ width: 95, textAlign: 'right' }}>Act. Total</LineTh>
              <LineTh style={{ width: 32, minWidth: 32, maxWidth: 32 }}></LineTh>
            </tr></thead><tbody>
              {outsourcingItems.map((os, index) => {
                const plannedQty = formData.plannedQty || 1;
                const actualQty = formData.actualQty || plannedQty;
                const estTotal = os.costMethod === 'Per Unit'
                  ? (os.estAmount || 0) * plannedQty
                  : (os.estAmount || 0);
                const actTotal = os.costMethod === 'Per Unit'
                  ? (os.actualAmount || 0) * actualQty
                  : (os.actualAmount || 0);
                return (
                <tr key={index}>
                  <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                  <LineTd>
                    <VendorSelect vendors={vendors} selectedVendorId={os.vendorId}
                      onChange={(vendorId) => handleOutsourcingChange(index, 'vendorId', vendorId)} />
                  </LineTd>
                  <LineTd><LineInput value={os.description} onChange={e => handleOutsourcingChange(index, 'description', e.target.value)} placeholder="Process details" /></LineTd>
                  <LineTd>
                    <LineSelect value={os.costMethod} onChange={e => handleOutsourcingChange(index, 'costMethod', e.target.value)}>
                      {COST_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </LineSelect>
                  </LineTd>
                  <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{formatAmount(os.estAmount || 0)}</LineTd>
                  <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(estTotal)}</LineTd>
                  <LineTd><LineInput type="number" min="0" step="0.01" value={os.actualAmount} onChange={e => handleOutsourcingChange(index, 'actualAmount', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
                  <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(actTotal)}</LineTd>
                  <LineTd style={{ textAlign: 'center' }}><RemoveLineBtn onClick={() => removeOutsourcing(index)}><FiX /></RemoveLineBtn></LineTd>
                </tr>
                );
              })}
            </tbody></LineTable></OverflowVisible>
          )}
        </FormCard>

        {/* Cost Summary - Estimated vs Actual */}
        <FormCard>
          <SectionTitle>Cost Summary</SectionTitle>
          <CostComparisonGrid>
            <CostCompRow>
              <CostCompLabel></CostCompLabel>
              <CostCompHeader>Estimated</CostCompHeader>
              <CostCompHeader>Actual</CostCompHeader>
              <CostCompHeader>Variance</CostCompHeader>
            </CostCompRow>
            <CostCompRow>
              <CostCompLabel>Material Cost</CostCompLabel>
              <CostCompValue>{formatAmount(estCostSummary.materialCost)}</CostCompValue>
              <CostCompValue>{formatAmount(actualCostSummary.materialCost)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(actualCostSummary.materialCost - estCostSummary.materialCost) > 0}>{formatAmount(actualCostSummary.materialCost - estCostSummary.materialCost)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow>
              <CostCompLabel>Overhead Cost</CostCompLabel>
              <CostCompValue>{formatAmount(estCostSummary.overheadCost)}</CostCompValue>
              <CostCompValue>{formatAmount(actualCostSummary.overheadCost)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(actualCostSummary.overheadCost - estCostSummary.overheadCost) > 0}>{formatAmount(actualCostSummary.overheadCost - estCostSummary.overheadCost)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow>
              <CostCompLabel>Outsourcing Cost</CostCompLabel>
              <CostCompValue>{formatAmount(estCostSummary.outsourcingCost)}</CostCompValue>
              <CostCompValue>{formatAmount(actualCostSummary.outsourcingCost)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(actualCostSummary.outsourcingCost - estCostSummary.outsourcingCost) > 0}>{formatAmount(actualCostSummary.outsourcingCost - estCostSummary.outsourcingCost)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow $highlight>
              <CostCompLabel>Total Cost</CostCompLabel>
              <CostCompValue>{formatAmount(estCostSummary.totalCost)}</CostCompValue>
              <CostCompValue>{formatAmount(actualCostSummary.totalCost)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(actualCostSummary.totalCost - estCostSummary.totalCost) > 0}>{formatAmount(actualCostSummary.totalCost - estCostSummary.totalCost)}</VarianceText></CostCompValue>
            </CostCompRow>
            <CostCompRow $primary>
              <CostCompLabel>Cost Per Unit</CostCompLabel>
              <CostCompValue>{formatAmount(estCostSummary.costPerUnit)}</CostCompValue>
              <CostCompValue>{formatAmount(actualCostSummary.costPerUnit)}</CostCompValue>
              <CostCompValue><VarianceText $negative={(actualCostSummary.costPerUnit - estCostSummary.costPerUnit) > 0}>{formatAmount(actualCostSummary.costPerUnit - estCostSummary.costPerUnit)}</VarianceText></CostCompValue>
            </CostCompRow>
          </CostComparisonGrid>
        </FormCard>

        {/* Notes */}
        <FormCard>
          <SectionTitle>Notes</SectionTitle>
          <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
        </FormCard>
      </>
    );
  };

  // ======================== SHORTCUTS & RENDER ========================

  useDocumentShortcuts({ onNew: canEditPage ? handleCreate : undefined, onSave: canEditPage ? () => handleSave('Planned') : undefined, viewMode, saving });

  return (
    <PageLayout pageTitle="Production Order" breadcrumb="Store / Production Order">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ LOCAL STYLED COMPONENTS ============

const AccountSelect = styled.select`
  width:100%;padding:6px 8px;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
  font-size:${typography.fontSize.xs};color:${colors.text.primary};background:${colors.background.default};
  max-width:220px;
  &:focus{border-color:${colors.primary.main};outline:none;}
`;

const YieldBadge = styled.span`
  display:inline-block;padding:2px 8px;border-radius:10px;font-size:${typography.fontSize.xs};
  font-weight:${typography.fontWeight.semibold};
  background:${props => props.$good ? colors.success.main + '20' : colors.warning.main + '20'};
  color:${props => props.$good ? colors.success.dark || colors.success.main : colors.warning.dark || colors.warning.main};
`;

const VarianceText = styled.span`
  color:${props => props.$negative ? colors.error.main : colors.success.main};
  font-weight:${typography.fontWeight.medium};
`;

const CostComparisonGrid = styled.div`
  display:flex;flex-direction:column;gap:2px;padding:${spacing.sm} 0;
`;

const CostCompRow = styled.div`
  display:grid;grid-template-columns:1.5fr 1fr 1fr 1fr;gap:${spacing.sm};
  padding:${spacing.sm} ${spacing.md};border-radius:${borderRadius.sm};
  background:${props => props.$primary ? colors.primary.main + '10' : props.$highlight ? colors.warning.main + '08' : 'transparent'};
  border-bottom:${props => props.$highlight || props.$primary ? `2px solid ${props.$primary ? colors.primary.main + '30' : colors.warning.main + '20'}` : `1px solid ${colors.border.light}`};
  &:first-child{border-bottom:2px solid ${colors.border.light};font-weight:${typography.fontWeight.semibold};}
`;

const CostCompLabel = styled.div`
  font-size:${typography.fontSize.sm};color:${colors.text.secondary};font-weight:${typography.fontWeight.medium};
  display:flex;align-items:center;
`;

const CostCompHeader = styled.div`
  font-size:${typography.fontSize.sm};color:${colors.text.primary};text-align:right;
  font-weight:${typography.fontWeight.semibold};
`;

const CostCompValue = styled.div`
  font-size:${typography.fontSize.sm};color:${colors.text.primary};text-align:right;
  font-weight:${typography.fontWeight.medium};
`;

const EmptySection = styled.div`
  padding:${spacing.lg};text-align:center;color:${colors.text.tertiary};font-size:${typography.fontSize.sm};
  border:1px dashed ${colors.border.light};border-radius:${borderRadius.sm};margin:${spacing.sm} 0;
`;

const OverflowVisible = styled.div`
  overflow:visible;border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
`;

export default ProductionOrder;
